<script lang="ts">
	import type { MetaData, FaqItem, ListItem, DatasetSchema } from "#lib/types/metaData.js";
	import type { Breadcrumb } from "#lib/types/breadcrumb.js";
	import { page } from "$app/state";
	import MetaTags from "./MetaTags.svelte";
	import JsonLd from "./JsonLd.svelte";
	import { canonicalUrl } from "#lib/siteUrl.js";

	const getTitle = (title = data?.title) => {
		// No title: return "Ishavskraft", append pathname if not root
		if (!title) {
			return `Ishavskraft${page?.url?.pathname !== "/" ? ` – ${page?.url?.pathname}` : ""}`;
		} else {
			title = title.trim();
			// remove trailing period
			title = title?.replace(/\.$/, "");
			return title;
		}
	};

	const getCanonicalUrl = (): string => {
		return data?.canonical || canonicalUrl(page?.url?.pathname);
	};

	interface Props {
		data?: MetaData | null;
		jsonLd?: boolean;
		faq?: FaqItem[] | null;
		reviews?:
			{ title: string; body: string; stars: number; date?: string; author?: string }[] | null;
		/** Include Organization schema - recommended for homepage */
		includeOrganization?: boolean;
		/** Breadcrumbs for BreadcrumbList schema */
		breadcrumbs?: Breadcrumb[] | null;

		itemList?: ListItem[] | null;
		/** Dataset schema for pages presenting structured data (e.g. spot prices) */
		dataset?: DatasetSchema | null;
	}

	let {
		data = null,
		jsonLd = false,
		faq = null,
		reviews = null,
		includeOrganization = false,
		breadcrumbs = null,
		itemList = null,
		dataset = null,
	}: Props = $props();

	// Sanity's `seo.description` is a multi-row text field, so editors leave
	// trailing newlines in it. Normalize once for both meta tags and JSON-LD.
	//
	// `dateModified` is fed from `lastReviewed` so JSON-LD and the og tags
	// agree on one editorial answer to "when was this last changed".
	const normalized = $derived(
		data
			? { ...data, description: data.description?.trim(), dateModified: data.lastReviewed }
			: data
	);
</script>

<!-- TODO: add twitter meta value (same for all pages) -->
<MetaTags
	title={(data?.title || getTitle()).trim()}
	titleValue={getTitle()}
	description={normalized?.description}
	author={data?.author}
	image={data?.image ||
		"https://cdn.sanity.io/images/n5xeg4df/ishavskraft-001/0fcb1907c3ef844aa2f5accb844c83c415f12e7c-4592x3056.jpg?rect=0,323,4592,2411&w=1200&h=630&fm=jpg&q=80&fit=crop"}
	imageAlt={data?.imageAlt}
	imageWidth={data?.imageWidth}
	imageHeight={data?.imageHeight}
	keywords={data?.keywords}
	site={data?.site}
	type={data?.type || "article"}
	canonical={getCanonicalUrl()}
	noIndex={data?.noIndex}
	noFollow={data?.noFollow}
	lastReviewed={data?.lastReviewed}
/>

{#if jsonLd}
	<JsonLd
		data={normalized}
		{faq}
		{reviews}
		{includeOrganization}
		{breadcrumbs}
		{itemList}
		{dataset}
	/>
{/if}
